import type { UseFetchOptions } from '#app'
import { defu } from 'defu'

import type { BasePaginationTypes } from '~/components/base/base-pagination/base-pagination.type'
import { applyRequestId } from '../utils/request-id-egress'

const LAST_MODIFIED_HEADER = 'last-modified'

function setPageLastModified(
    event: ReturnType<typeof useRequestEvent>,
    lastModified: string | null
) {
    if (!event || !lastModified) {
        return
    }

    const timestamp = Date.parse(lastModified)

    if (Number.isNaN(timestamp)) {
        return
    }

    const context = event.context as typeof event.context & {
        lastModified?: number
    }

    if (
        context.lastModified !== undefined &&
        context.lastModified >= timestamp
    ) {
        return
    }

    context.lastModified = timestamp
}

export interface Include<T> {
    data: T
    meta?: {
        include?: string[]
        custom?: unknown
        pagination?: BasePaginationTypes
        [key: string]: unknown
    }
}

export interface Page<T> {
    data: T
    meta: {
        include: string[]
        custom: unknown
        pagination?: BasePaginationTypes
        [key: string]: unknown
    }
}
interface UseApiFetchOptions<T> extends UseFetchOptions<T> {
    clear?: boolean
    withoutData?: boolean
    client?: boolean
    useLastModified?: boolean
}

export async function useApiFetch<T>(
    url: string,
    options: UseApiFetchOptions<T> = {}
) {
    const userAuth = useCookie('access_token')
    const config = useRuntimeConfig()
    const originalOnResponse = options.onResponse
    const event = import.meta.server ? useRequestEvent() : undefined
    const requestId = event?.context.cromiRequestId
    const nuxtApp = useNuxtApp()
    const route = useRoute()
    const method = String(options.method || 'GET').toUpperCase()
    const retryableStatuses = [408, 409, 425, 429, 500, 502, 503, 504]

    function reportApiError(context: any, type: 'api' | 'network' = 'api') {
        if (!import.meta.client) return

        const reportFrontendError = nuxtApp.$reportFrontendError as
            | ((input: Record<string, unknown>) => void)
            | undefined

        reportFrontendError?.({
            type,
            status: context?.response?.status,
            message: context?.error?.message || context?.response?.statusText,
            url: context?.request?.toString?.() || url,
            route: route.fullPath,
        })
    }

    const defaults: UseApiFetchOptions<T> = {
        withoutData: 'withoutData' in options ? options.withoutData : false,
        clear: false,
        baseURL: options.clear ? '' : config.public.api_url,
        key: config.public.api_url + url,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Authorization: userAuth.value ? `Bearer ${userAuth.value}` : '',
            'Office-Cromi-Domain': config.public.website as string,
        },
        transform: function (data: any) {
            if (Object.keys(data).includes('data') && defaults.withoutData) {
                return data.data
            } else {
                return data
            }
        },
        async onResponse(context) {
            if (options.useLastModified) {
                const { response } = context

                setPageLastModified(
                    event,
                    response.headers.get(LAST_MODIFIED_HEADER)
                )

                if (!originalOnResponse) {
                    return
                }

                const hooks = Array.isArray(originalOnResponse)
                    ? originalOnResponse
                    : [originalOnResponse]

                for (const hook of hooks) {
                    await hook(context)
                }
            }
        },
        retry: method === 'GET' ? 1 : 0,
        retryStatusCodes: retryableStatuses,
        onRequestError(context: any) {
            reportApiError(context, 'network')
            if (typeof options.onRequestError === 'function') {
                options.onRequestError(context)
            }
        },
        onResponseError(context: any) {
            reportApiError(context)
            if (typeof options.onResponseError === 'function') {
                options.onResponseError(context)
            }
        },
    }

    if (options?.client) {
        defaults.body = {
            client_id: Number(config.public.client_id),
            client_secret: config.public.client_secret,
        }
    }
    const params = defu(options, defaults)
    params.onResponse = defaults.onResponse
    if (import.meta.server && !options.$fetch) {
        const callerOnRequest = params.onRequest
        params.onRequest = async (context) => {
            const hooks = callerOnRequest
                ? Array.isArray(callerOnRequest)
                    ? callerOnRequest
                    : [callerOnRequest]
                : []
            for (const hook of hooks) await hook(context)
            applyRequestId(context, requestId, config.public.api_url)
        }
    } else if (import.meta.server && event) {
        // Custom transports need not implement ofetch hooks; do not inject an ID.
        event.context.cromiRequestIdEgressGap =
            'custom_transport_not_correlated'
    }

    return useFetch(url, params)
}
